<template>
  <b-modal
    v-model="isOpenModel"
    width="500px"
    scroll="keep"
    :hide-header="true"
    :hide-footer="true"
    class="usage-summary-modal">
    <div class="usage-modal">
      <div class="modal-header">
        <h3 class="modal-title">
          Usage Summary
        </h3>
        <button
          class="button is-light close-button"
          @click="closeModal">
          <b-icon icon="close" />
        </button>
      </div>

      <div class="modal-content">
        <!-- Attribute Section -->
        <div class="attribute-section">
          <div class="attribute-info">
            <div class="attribute-name-wrapper">
              <span class="attribute-label">Attribute</span>
              <span class="attribute-name">{{ attributeName }}</span>
            </div>
            <span class="reference-count">{{ totalReferences }} references</span>
          </div>
        </div>

        <!-- Search Bar -->
        <div class="search-section">
          <div class="search-input-wrapper">
            <b-icon
              icon="magnify"
              class="search-icon" />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search"
              class="search-input" />
          </div>
        </div>

        <!-- Usage Sections -->
        <div class="usage-sections">
          <div v-if="loading">
            <b-skeleton
              width="100%"
              :count="4"
              height="40" />
          </div>
          <div v-else>
            <div v-if="usageReport">
              <div
                v-for="usageType in usageTypes"
                :key="usageType.key"
                class="usage-type-section">
                <div
                  class="usage-type-header"
                  @click="toggleSection(usageType.key)">
                  <span class="usage-type-label">{{ usageType.label }}</span>
                  <div class="usage-type-count">
                    <span class="count-number">{{
                      getFilteredUsages(usageType.key).length
                    }}</span>
                    <b-icon
                      class="chevron-icon"
                      :icon="
                        openSections.includes(usageType.key)
                          ? 'chevron-up'
                          : 'chevron-down'
                      " />
                  </div>
                </div>

                <div
                  v-show="openSections.includes(usageType.key)"
                  class="usage-items">
                  <UsageItem
                    v-for="(usage, idx) in getFilteredUsages(usageType.key)"
                    :key="idx"
                    :usage="usage"
                    :source-id="sourceId"
                    :source-type="sourceType" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
  import UsageItem from './UsageItem.vue'
  import FuzzySearch from 'fuzzy-search'

  export default {
    name: 'UsageSummary',
    components: {
      UsageItem,
    },
    props: {
      isOpen: { type: Boolean, default: false, },
      loading: { type: Boolean, default: false, },
      usageReport: { type: Array, default: () => [], },
      attributeName: { type: String, default: '', },
      sourceId: { type: [String, Number,], default: null, },
      sourceType: { type: String, default: '', },
    },
    data () {
      return {
        searchQuery: '',
        openSections: ['DASHBOARD_MODULE',], // Default open section
        usageTypes: [
          {
            key: 'EXPRESSION',
            label: 'Expression',
          },
          {
            key: 'DASHBOARD_MODULE',
            label: 'Dashboards',
          },
          {
            key: 'PDF_REPORT',
            label: 'Pdf Reports',
          },
          {
            key: 'ATTRIBUTE',
            label: 'Attributes',
          },
          {
            key: 'COMPANY_EXPRESSION',
            label: 'Company Expressions',
          },
          {
            key: 'COMPANY_MEASURE',
            label: 'Company Measures',
          },
          {
            key: 'COMPANY_DIMENSION',
            label: 'Company Dimensions',
          },
          {
            key: 'COMPANY_FILTER',
            label: 'Company Filters',
          },
          {
            key: 'MEASURE_GROUP',
            label: 'Measure Groups',
          },
          {
            key: 'DIMENSION_GROUP',
            label: 'Dimension Groups',
          },
          {
            key: 'COMPANY_SCOPE',
            label: 'Company Scopes',
          },
        ],
      }
    },
    computed: {
      isOpenModel: {
        get () {
          return this.isOpen
        },
        set (isOpen) {
          this.$emit('update:isOpen', isOpen)
        },
      },
      totalReferences () {
        return Object.values(this.usageTypes).reduce((total, type) => {
          return total + this.getFilteredUsages(type.key).length
        }, 0)
      },
    },
    methods: {
      closeModal () {
        this.$emit('update:isOpen', false)
      },
      toggleSection (sectionKey) {
        const index = this.openSections.indexOf(sectionKey)
        if (index > -1) {
          this.openSections.splice(index, 1)
        } else {
          this.openSections.push(sectionKey)
        }
      },
      getFilteredUsages (usageTypeKey) {
        const usages = this.usageReport.filter(
          (usage) => usage.referencer_type === usageTypeKey
        )

        if (!this.searchQuery) return usages

        const fuzzySearch = new FuzzySearch(usages, ['referencer_name',], {
          caseSensitive: false,
        })
        return fuzzySearch.search(this.searchQuery)
      },
      renderPath (path) {
        let rendered = ''
        for (let p of path) {
          if (!isNaN(p)) {
            rendered = rendered + `[${p}]`
          } else if (
            this.usageTypes.map((uT) => uT.key).some((k) => p.includes(k))
          ) {
            rendered = rendered + `=>${p}`
          } else {
            rendered = rendered + `.${p}`
          }
        }
        return rendered
      },
    },
  }
</script>

<style lang="scss" scoped>
.usage-summary-modal {
  :deep(.modal-content) {
    border-radius: 8px;
    overflow: hidden;
  }
}

.usage-modal {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 24px;
    border-bottom: 1px solid #e5e7eb;

    .modal-title {
      font-size: 14px;
      font-weight: 300;
      color: #111827;
      margin: 0;
    }

    .close-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 2px;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      color: #6b7280;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: #f3f4f6;
        color: #374151;
      }
    }
  }

  .modal-content {
    padding: 20px;

    .attribute-section {
      margin-bottom: 10px;

      .attribute-info {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .attribute-name-wrapper {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .attribute-label {
            font-size: 12px;
            color: #6b7280;
            text-transform: uppercase;
          }

          .attribute-name {
            font-size: 16px;
            font-weight: 400;
            color: #111827;
          }
        }

        .reference-count {
          background-color: #f3f4f6;
          color: #6b7280;
          padding: 4px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
        }
      }
    }

    .search-section {
      padding: 0px 0px;
      margin-bottom: 10px;

      .search-input-wrapper {
        position: relative;
        display: flex;
        align-items: center;

        .search-icon {
          position: absolute;
          left: 12px;
          color: #9ca3af;
          font-size: 16px;
        }

        .search-input {
          width: 100%;
          padding: 8px 12px 8px 36px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s;

          &:focus {
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          &::placeholder {
            color: #9ca3af;
          }
        }
      }
    }

    .usage-sections {
      max-height: 400px;
      overflow-y: auto;
      border: 1px solid #e5e7eb;
      border-radius: 8px;

      .usage-type-section {
        border-bottom: 1px solid #e5e7eb;

        &:last-child {
          border-bottom: none;
        }

        .usage-type-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 16px;
          cursor: pointer;
          transition: background-color 0.2s;

          &:hover {
            background-color: #f9fafb;
          }

          .usage-type-label {
            font-size: 15px;
            font-weight: 300;
            color: #111827;
          }

          .usage-type-count {
            display: flex;
            align-items: center;
            gap: 8px;

            .count-number {
              background-color: #f3f4f6;
              color: #6b7280;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: 500;
              min-width: 20px;
              text-align: center;
            }

            .chevron-icon {
              color: #6b7280;
              font-size: 14px;
              transition: transform 0.2s;
            }
          }
        }

        .usage-items {
          position: relative;
          padding-left: 0px;
          margin-left: 30px;
          border-left: 1px solid #e5e7eb;
        }
      }
    }
  }
}
</style>
